import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_EMAIL = "lengellejacques@outlook.fr";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "", // honeypot
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (field) => (e) => {
    setForm((s) => ({ ...s, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((s) => {
        const next = { ...s };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Merci d'indiquer votre nom.";
    if (!EMAIL_REGEX.test(form.email.trim())) errs.email = "Adresse email invalide.";
    if (form.message.trim().length < 20)
      errs.message = "Votre message doit comporter au moins 20 caractères.";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setStatus("loading");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        website: form.website, // honeypot
      });
      if (data?.ok) {
        setStatus("success");
        setForm({ name: "", email: "", subject: "", message: "", website: "" });
      } else {
        setStatus("error");
        setErrorMsg(data?.error || "Une erreur est survenue.");
      }
    } catch (err) {
      setStatus("error");
      const apiError = err?.response?.data?.error;
      if (err?.response?.status === 429) {
        setErrorMsg(
          apiError || "Trop de tentatives, réessayez dans quelques minutes."
        );
      } else {
        setErrorMsg(
          apiError ||
            `Une erreur est survenue, veuillez réessayer ou nous écrire directement à ${CONTACT_EMAIL}.`
        );
      }
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-28 md:py-40 border-t border-[#E5E5E5]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 md:mb-20 max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="accent-bar-sm" aria-hidden="true" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#6B7280] font-medium">
              Contact
            </span>
          </div>
          <h2
            data-testid="contact-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A]"
          >
            <span className="italic font-normal">Contact</span>.
          </h2>
          <p
            data-testid="contact-subtitle"
            className="mt-6 text-base md:text-lg text-[#374151] font-light max-w-xl leading-relaxed"
          >
            Échangeons sur votre projet.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — Coordonnées */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-[#6B7280] font-medium mb-4">
              <Mail size={14} strokeWidth={1.5} />
              <span>Email</span>
            </div>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-mailto-link"
              className="font-serif text-2xl md:text-3xl text-[#0A0A0A] hover:text-[#E10600] transition-colors break-all leading-tight inline-block"
            >
              {CONTACT_EMAIL}
            </a>
            <span
              className="block w-12 h-[2px] bg-[#E10600] mt-6 mb-8"
              aria-hidden="true"
            />
            <p className="text-[#374151] font-light leading-relaxed text-base max-w-md">
              Nous étudions chaque demande avec confidentialité et vous
              répondons sous 48&nbsp;h ouvrées.
            </p>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="bg-[#FAFAFA] border border-[#E5E5E5] p-8 md:p-10 space-y-6"
            >
              {/* Honeypot — visually hidden, not tabbable */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={update("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute opacity-0 -left-[9999px] -top-[9999px] w-0 h-0 pointer-events-none"
                data-testid="contact-honeypot"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  id="contact-name"
                  label="Nom"
                  testId="contact-input-name"
                  required
                  value={form.name}
                  onChange={update("name")}
                  error={errors.name}
                  disabled={status === "loading"}
                />
                <Field
                  id="contact-email"
                  label="Email"
                  type="email"
                  testId="contact-input-email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  error={errors.email}
                  disabled={status === "loading"}
                />
              </div>
              <Field
                id="contact-subject"
                label="Sujet"
                testId="contact-input-subject"
                value={form.subject}
                onChange={update("subject")}
                error={errors.subject}
                disabled={status === "loading"}
              />
              <TextareaField
                id="contact-message"
                label="Message"
                testId="contact-input-message"
                required
                value={form.message}
                onChange={update("message")}
                error={errors.message}
                disabled={status === "loading"}
                rows={5}
                helper="Au moins 20 caractères."
              />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-2">
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "loading"}
                  className="group inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#E10600] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" strokeWidth={1.5} />
                      <span>Envoi en cours…</span>
                    </>
                  ) : (
                    <>
                      <span>Envoyer</span>
                      <ArrowRight
                        size={16}
                        strokeWidth={1.5}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>

                {status === "success" && (
                  <div
                    data-testid="contact-success"
                    role="status"
                    className="inline-flex items-center gap-2 text-sm text-[#1F7A3A] font-medium"
                  >
                    <CheckCircle2 size={16} strokeWidth={1.5} />
                    <span>
                      Message envoyé. Nous vous recontactons rapidement.
                    </span>
                  </div>
                )}

                {status === "error" && (
                  <div
                    data-testid="contact-error"
                    role="alert"
                    className="inline-flex items-start gap-2 text-sm text-[#E10600] font-medium max-w-sm"
                  >
                    <AlertCircle size={16} strokeWidth={1.5} className="mt-[2px] flex-shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}
              </div>

              <p
                data-testid="contact-rgpd-notice"
                className="text-xs text-[#6B7280] font-light leading-relaxed pt-4 border-t border-[#E5E5E5]"
              >
                Les informations transmises via ce formulaire sont utilisées
                uniquement pour vous recontacter. Conformément au RGPD, vous
                disposez d'un droit d'accès et de suppression sur vos
                données.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ---------- Field components ----------
const baseFieldClass =
  "w-full bg-white border border-[#E5E5E5] px-4 py-3 text-[#0A0A0A] text-base font-light placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#0A0A0A] transition-colors disabled:opacity-60";

const Field = ({ id, label, type = "text", testId, required, value, onChange, error, disabled }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-xs tracking-[0.2em] uppercase text-[#6B7280] font-medium mb-2"
    >
      {label}
      {required && <span className="text-[#E10600] ml-1">*</span>}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      data-testid={testId}
      aria-invalid={error ? "true" : "false"}
      aria-describedby={error ? `${id}-error` : undefined}
      className={`${baseFieldClass} ${error ? "border-[#E10600]" : ""}`}
    />
    {error && (
      <p id={`${id}-error`} className="mt-1 text-xs text-[#E10600] font-medium">
        {error}
      </p>
    )}
  </div>
);

const TextareaField = ({ id, label, testId, required, value, onChange, error, disabled, rows = 5, helper }) => (
  <div>
    <label
      htmlFor={id}
      className="block text-xs tracking-[0.2em] uppercase text-[#6B7280] font-medium mb-2"
    >
      {label}
      {required && <span className="text-[#E10600] ml-1">*</span>}
    </label>
    <textarea
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      rows={rows}
      data-testid={testId}
      aria-invalid={error ? "true" : "false"}
      aria-describedby={error ? `${id}-error` : `${id}-helper`}
      className={`${baseFieldClass} resize-none ${error ? "border-[#E10600]" : ""}`}
    />
    {error ? (
      <p id={`${id}-error`} className="mt-1 text-xs text-[#E10600] font-medium">
        {error}
      </p>
    ) : helper ? (
      <p id={`${id}-helper`} className="mt-1 text-xs text-[#9CA3AF] font-light">
        {helper}
      </p>
    ) : null}
  </div>
);
